import React, { useState } from 'react';
import axios from 'axios';
import { Row, Col, Form, Button, Badge, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import dayjs from '../../../lib/day';
import PrivateLayout from '../../layouts/PrivateLayout';
import Panel from '../../common/Panel';
import DataTable from '../../common/DataTable';
import Pagination from '../../common/Pagination';
import { useFetchVaPayments } from '../../../services/vaPayments';
import { COLUMNS } from '../../../components/dashboard/admin/SalesDashboard';

const MANUAL_REASON_LABELS = {
  legacy_billing: 'Legacy',
  repeated_call_failures: 'Failed Calls',
  missing_qbo_customer: 'Missing QBO Customer',
  invoice_creation_failed: 'Invoice Creation Failed',
  invoice_flow_failed: 'Invoice Flow Failed',
  missing_inferred_rating: 'Missing Rating',
  initial_rating_baseline: 'Initial Rating Baseline',
  non_positive_fee_delta: 'No Positive Fee Delta',
  open_sa_correction: 'Open Agreement Correction',
  non_compensation_payment_type: 'Non-Compensation Payment',
  no_exact_matrix_match: 'No Matrix Match',
  ambiguous_matrix_match: 'Ambiguous Matrix Match',
  matrix_validation_failed: 'Matrix Validation Failed',
};

const formatManualReason = (reason) => {
  if (!reason) return '';
  if (MANUAL_REASON_LABELS[reason]) return MANUAL_REASON_LABELS[reason];
  return String(reason)
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getManualReviewReason = (row) => {
  if (row.isLegacyBilling || row.manualReason === 'legacy_billing') return 'Legacy';
  if (row.manualReason) return formatManualReason(row.manualReason);
  if (row.manualReviewRequired) return 'Manual Review';
  return '';
};

const getManualReviewVariant = (row) => {
  if (row.isLegacyBilling || row.manualReason === 'legacy_billing') return 'dark';
  if (row.manualReason === 'repeated_call_failures') return 'danger';
  return 'warning';
};

const VaPayments = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    page: 1,
    pageSize: 25,
    startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
    endDate: dayjs().endOf('month').format('YYYY-MM-DD'),
    gtPrev: '', // '', 'true', 'false'
    legacy: '', // '', 'true', 'false'
    manual: '', // '', 'true', 'false'
    name: '',
  });

  const { isLoading, data } = useFetchVaPayments(
    filters.page,
    filters.pageSize,
    filters.startDate,
    filters.endDate,
    filters.gtPrev || undefined,
    filters.name || undefined,
    filters.legacy || undefined,
    filters.manual || undefined,
  );

  const totals = data && data.totals ? data.totals : {};
  const rows = data && data.payments ? data.payments : [];
  const pagination = data && data.pagination ? data.pagination : { total: 0 };

  const refreshNumbers = async () => {
    try {
      await axios.post('/retell-admin/numbers/refresh');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const renderExpandedRow = (row) => {
    const payments = row.payments || [];
    if (!payments.length) return null;
    const paymentsSorted = [...payments].sort((a, b) => {
      const ad = a.vaPaymentCallDate ? new Date(a.vaPaymentCallDate).getTime() : 0;
      const bd = b.vaPaymentCallDate ? new Date(b.vaPaymentCallDate).getTime() : 0;
      return bd - ad; // desc by call date
    });
    return (
      <div>
        <div className="mb-2"><strong>Payment History</strong></div>
        <Table size="sm" bordered={true}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Amount</th>
              <th>Call Date</th>
              <th>Cost (cents)</th>
              <th>Retell</th>
            </tr>
          </thead>
          <tbody>
            {paymentsSorted.map((p) => (
              <tr key={p.vaPaymentId}>
                <td>{p.vaPaymentDate ? dayjs.utc(p.vaPaymentDate).format('MM/DD/YYYY') : ''}</td>
                <td>{p.vaPaymentAmount != null ? Number(p.vaPaymentAmount) : ''}</td>
                <td>{p.vaPaymentCallDate ? dayjs.utc(p.vaPaymentCallDate).format('MM/DD/YYYY HH:mm') : ''}</td>
                <td>{p.vaPaymentCost != null ? Math.round(Number(p.vaPaymentCost)) : ''}</td>
                <td>
                  {p.vaPaymentRetellCallId ? (
                    <a
                      href={`https://dashboard.retellai.com/call-history?history=${p.vaPaymentRetellCallId}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View
                    </a>
                  ) : (
                    ''
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  return (
    <PrivateLayout>
      <Row>
        <Col>
          <Panel>
            <Row>
              <Col>
                <h2>VA Calls</h2>
              </Col>
              <Col xs={12} md={8} className="text-end">
                <div className="d-flex gap-2 justify-content-end align-items-center flex-wrap">
                  <Form.Control
                    type="search"
                    placeholder="Search by name or email"
                    value={filters.name}
                    onChange={(e) => setFilters({ ...filters, page: 1, name: e.target.value })}
                    style={{ maxWidth: 260 }}
                  />
                  <Form.Control
                    type="date"
                    value={filters.startDate}
                    onChange={(e) =>
                      setFilters({ ...filters, page: 1, startDate: e.target.value })
                    }
                    style={{ maxWidth: 160 }}
                  />
                  <Form.Control
                    type="date"
                    value={filters.endDate}
                    onChange={(e) =>
                      setFilters({ ...filters, page: 1, endDate: e.target.value })
                    }
                    style={{ maxWidth: 160 }}
                  />
                  <Form.Select
                    value={filters.gtPrev}
                    onChange={(e) => setFilters({ ...filters, page: 1, gtPrev: e.target.value })}
                    style={{ maxWidth: 240 }}
                  >
                    <option value="">All payment changes</option>
                    <option value="true">Increases only</option>
                    <option value="false">No increase</option>
                  </Form.Select>
                  <Form.Select
                    value={filters.legacy}
                    onChange={(e) => setFilters({ ...filters, page: 1, legacy: e.target.value })}
                    style={{ maxWidth: 200 }}
                  >
                    <option value="">All legacy states</option>
                    <option value="true">Legacy only</option>
                    <option value="false">Non-legacy only</option>
                  </Form.Select>
                  <Form.Select
                    value={filters.manual}
                    onChange={(e) => setFilters({ ...filters, page: 1, manual: e.target.value })}
                    style={{ maxWidth: 200 }}
                  >
                    <option value="">All manual review states</option>
                    <option value="true">Manual review queue only</option>
                    <option value="false">Not in manual review queue</option>
                  </Form.Select>
                  <Button variant="outline-primary" onClick={refreshNumbers}>
                    Refresh Retell Numbers
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={() =>
                      setFilters((prev) => ({
                        ...prev,
                        page: 1,
                        legacy: 'true',
                        manual: '',
                      }))
                    }
                  >
                    Legacy Billing
                  </Button>
                  <Button variant="outline-secondary" onClick={() => navigate('/admin/va_calls')}>
                    VA Call Attempts
                  </Button>
                  <Button variant="outline-secondary" onClick={() => navigate('/admin/va_failed_calls')}>
                    Failed Calls Queue
                  </Button>
                  <Button
                    variant="outline-success"
                    onClick={async () => {
                      try {
                        const params = new URLSearchParams();
                        if (filters.startDate) params.set('startDate', filters.startDate);
                        if (filters.endDate) params.set('endDate', filters.endDate);
                        if (filters.gtPrev) params.set('gtPrev', filters.gtPrev);
                        if (filters.legacy) params.set('legacy', filters.legacy);
                        if (filters.manual) params.set('manual', filters.manual);
                        if (filters.name) params.set('name', filters.name);
                        const response = await axios.get(`/va-payments/export?${params.toString()}`, {
                          responseType: 'blob',
                        });
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'va-payments.csv');
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                      } catch (e) {
                        console.error('CSV download failed:', e);
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faDownload} /> CSV
                  </Button>
                </div>
              </Col>
            </Row>
            <Row className="mt-3">
              <Col>
                <div className="d-flex gap-4">
                  <div>
                    <div className="text-muted">Sum of Latest Amounts</div>
                    <div className="fs-5 fw-bold">
                      ${Number(totals.totalAmount || 0).toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted">Sum of Latest Costs (in cents)</div>
                    <div className="fs-5 fw-bold">{Math.round(Number(totals.totalCost || 0))}</div>
                  </div>
                  <div>
                    <div className="text-muted">Users</div>
                    <div className="fs-5 fw-bold">{pagination.total || 0}</div>
                  </div>
                  <div>
                    <div className="text-muted">Draft invoices</div>
                    <div className="fs-5 fw-bold">{Number(totals.draftInvoices || 0)}</div>
                  </div>
                  <div>
                    <div className="text-muted">Manual / legacy review</div>
                    <div className="fs-5 fw-bold">
                      {Number(totals.manualReviewReasonCount || 0)}
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            <br />

            <DataTable
              columns={[
                {
                  header: 'User Name',
                  key: 'userName',
                  render: (row) => (
                    <a href={`/admin/profile/${row.claimId}`}>
                      {(row.userFirstName || '') + ' ' + (row.userLastName || '')}
                    </a>
                  ),
                },
                {
                  header: 'Claim Status',
                  key: 'claimStatus',
                  render: (row) => COLUMNS.find((c) => c.id === row.claimStatus)?.label,
                },
                {
                  header: 'Payment Date',
                  key: 'latestPayment.vaPaymentDate',
                  render: (row) =>
                    row.latestPayment?.vaPaymentDate
                      ? dayjs.utc(row.latestPayment.vaPaymentDate).format('MM/DD/YYYY')
                      : '',
                },
                {
                  header: 'Amount',
                  key: 'latestPayment.vaPaymentAmount',
                  render: (row) => (row.latestPayment?.vaPaymentAmount != null ? Number(row.latestPayment.vaPaymentAmount) : ''),
                },
                {
                  header: 'Call Date',
                  key: 'latestPayment.vaPaymentCallDate',
                  render: (row) =>
                    row.latestPayment?.vaPaymentCallDate
                      ? dayjs.utc(row.latestPayment.vaPaymentCallDate).format('MM/DD/YYYY HH:mm')
                      : '',
                },
                {
                  header: 'Cost (cents)',
                  key: 'latestPayment.vaPaymentCost',
                  render: (row) =>
                    row.latestPayment?.vaPaymentCost != null
                      ? Math.round(Number(row.latestPayment.vaPaymentCost))
                      : '',
                },
                {
                  header: 'Manual Review',
                  key: 'manualReviewRequired',
                  render: (row) => {
                    const reason = getManualReviewReason(row);
                    if (!reason) return <span className="text-muted">-</span>;
                    const variant = getManualReviewVariant(row);
                    return (
                      <Badge bg={variant} text={variant === 'warning' ? 'dark' : undefined}>
                        {reason}
                      </Badge>
                    );
                  },
                },
                {
                  header: 'Billing Status',
                  key: 'billingStatus',
                  render: (row) => {
                    if (!row.billingStatus) return <span className="text-muted">-</span>;
                    if (row.billingStatus === 'invoiced' || row.billingStatus === 'stripe_invoiced') return <Badge bg="success">Invoiced</Badge>;
                    if (row.billingStatus === 'needs_review') return <Badge bg="warning" text="dark">Needs Review</Badge>;
                    if (row.billingStatus === 'no_invoice') return <Badge bg="secondary">No Invoice</Badge>;
                    return <Badge bg="secondary">{row.billingStatus}</Badge>;
                  },
                },
                {
                  header: 'Rating Change',
                  key: 'ratingChange',
                  render: (row) => {
                    const from = row.ratingChange?.from;
                    const to = row.ratingChange?.to;
                    if (from == null && to == null) return <span className="text-muted">-</span>;

                    const fromNumber = from != null ? Number(from) : null;
                    const toNumber = to != null ? Number(to) : null;
                    const increased =
                      row.greaterThanPrevious ||
                      (fromNumber != null &&
                        toNumber != null &&
                        !Number.isNaN(fromNumber) &&
                        !Number.isNaN(toNumber) &&
                        toNumber > fromNumber);

                    return (
                      <div className="d-flex flex-column align-items-start gap-1">
                        {increased ? <Badge bg="success">Increase</Badge> : null}
                        <span className={increased ? 'text-success fw-semibold' : ''}>
                          {from != null ? `${from}%` : '?'}
                          {' -> '}
                          {to != null ? `${to}%` : '?'}
                        </span>
                      </div>
                    );
                  },
                },
              ]}
              busy={isLoading}
              data={rows}
              rowKey="claimId"
              renderExpandedRow={renderExpandedRow}
              onSortChange={() => {}}
            />

            {pagination.total > 0 ? (
              <Pagination
                page={filters.page}
                totalRows={pagination.total}
                rowsPerPage={filters.pageSize}
                onBack={() => setFilters({ ...filters, page: filters.page - 1 })}
                onForward={() => setFilters({ ...filters, page: filters.page + 1 })}
              />
            ) : null}
          </Panel>
        </Col>
      </Row>
    </PrivateLayout>
  );
};

export default VaPayments;
